import React, { useEffect, useState } from "react";
import { Form, Input, Select, Modal, message } from "antd";
import { NavLink } from "react-router-dom";
import dayjs from "dayjs";
import "./payment.css";

// RazorPay Payment Component
const RazorPayCheckout = ({ amount, customerDetails, gstDetails, onSuccess, onFailure }) => {
  const [loading, setLoading] = useState(false);

  const loadRazorPayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  // Create order on backend
  const createOrder = async () => {
    try {
      console.log('Creating order for amount:', amount);
      
      const orderData = {
        amount: amount,
        currency: 'INR',
        receipt: `villa_booking_${Date.now()}`,
        notes: {
          booking_type: 'villa_booking',
          customer_name: `${customerDetails.firstName} ${customerDetails.lastName}`.trim(),
          customer_email: customerDetails.email,
          customer_phone: customerDetails.phone,
          nights: customerDetails.nights || 0,
          amount_inr: amount,
          ...(gstDetails.gstNumber && {
            gst_number: gstDetails.gstNumber,
            company_name: gstDetails.companyName,
            company_address: gstDetails.companyAddress,
          }),
        },
      };

      console.log('Sending order data:', orderData);

      const response = await fetch('http://localhost:5002/api/create-order', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orderData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Order creation response:', data);

      if (data.success) {
        return data.order;
      } else {
        throw new Error(data.error || 'Failed to create order');
      }
    } catch (error) {
      console.error('Order creation failed:', error);
      throw error;
    }
  };

  // Verify payment on backend
  const verifyPayment = async (paymentResponse) => {
    try {
      const verificationData = {
        razorpay_order_id: paymentResponse.razorpay_order_id,
        razorpay_payment_id: paymentResponse.razorpay_payment_id,
        razorpay_signature: paymentResponse.razorpay_signature,
        booking_details: {
          customer_name: `${customerDetails.firstName} ${customerDetails.lastName}`.trim(),
          customer_email: customerDetails.email,
          customer_phone: customerDetails.phone,
          nights: customerDetails.nights,
          amount: amount,
          gst_details: gstDetails.gstNumber ? gstDetails : null,
        },
      };

      const response = await fetch('http://localhost:5002/api/verify-payment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(verificationData),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Payment verification failed:', error);
      throw error;
    }
  };

  const handlePayment = async () => {
    setLoading(true);
    
    try {
      // Load RazorPay SDK
      const res = await loadRazorPayScript();
      if (!res) {
        message.error('Failed to load RazorPay SDK. Please check your internet connection.');
        setLoading(false);
        return;
      }

      // Validate required fields
      if (!customerDetails.firstName || !customerDetails.email || !customerDetails.phone) {
        message.error('Please fill in all required customer details before proceeding.');
        setLoading(false);
        return;
      }

      // Create order on backend (required for RazorPay)
      console.log('Creating order on backend...');
      const order = await createOrder();
      
      if (!order || !order.id) {
        throw new Error('Failed to create order - no order ID received');
      }

      console.log('Order created successfully:', order);

      // Get the key from environment or use hardcoded test key as fallback
      const razorpayKey = process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_test_AdlmUunLCzQh70';
      
      console.log('Environment Key:', process.env.REACT_APP_RAZORPAY_KEY_ID);
      console.log('Using RazorPay Key:', razorpayKey);

      if (!razorpayKey) {
        throw new Error('RazorPay key is missing. Please check your environment configuration.');
      }

      // RazorPay options
      const options = {
        key: razorpayKey,
        amount: order.amount, // Use amount from order (already in paise)
        currency: order.currency,
        name: 'Gir Glades Villa',
        description: `Villa Booking - ${customerDetails.nights} nights`,
        order_id: order.id, // Required for proper RazorPay flow
        prefill: {
          name: `${customerDetails.firstName} ${customerDetails.lastName}`.trim(),
          email: customerDetails.email,
          contact: customerDetails.phone,
        },
        theme: {
          color: '#652220',
        },
        modal: {
          ondismiss: () => {
            setLoading(false);
            message.info('Payment cancelled by user');
          },
        },
        handler: async (response) => {
          console.log('Payment Success Response:', response);
          
          try {
            // Verify payment on backend
            setLoading(true);
            const verificationResult = await verifyPayment(response);
            
            if (verificationResult.success) {
              message.success('Payment completed and verified successfully!');
              onSuccess(response);
            } else {
              message.error('Payment verification failed. Please contact support.');
              onFailure({ error: { description: 'Payment verification failed' } });
            }
          } catch (verificationError) {
            console.error('Payment verification error:', verificationError);
            message.error('Payment verification failed. Please contact support.');
            onFailure({ error: { description: 'Payment verification failed' } });
          } finally {
            setLoading(false);
          }
        },
      };

      console.log('RazorPay Options:', {
        ...options,
        key: options.key ? 'KEY_PROVIDED' : 'NO_KEY'  // Don't log actual key
      });

      // Verify RazorPay is loaded
      if (!window.Razorpay) {
        throw new Error('RazorPay SDK not loaded properly');
      }
      
      const paymentObject = new window.Razorpay(options);
      
      paymentObject.on('payment.failed', (response) => {
        console.log('Payment Failed Response:', response);
        message.error(`Payment failed: ${response.error?.description || 'Unknown error'}`);
        onFailure(response);
        setLoading(false);
      });

      paymentObject.open();
    } catch (error) {
      console.error('Payment initiation error:', error);
      message.error(`Failed to initiate payment: ${error.message}`);
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <p className="text-sm text-gray-600 mb-4">
          Secure payment processed via RazorPay. Your payment details are encrypted and secure.
        </p>
      </div>
      
      <div className="p-4 bg-gray-50 rounded-md mb-4">
        <h3 className="font-medium mb-2">Payment Summary</h3>
        <div className="flex justify-between text-sm mb-1">
          <span>Subtotal</span>
          <span>₹{(amount / 1.12).toFixed(2)}</span>
        </div>
        <div className="flex justify-between text-sm mb-1">
          <span>Taxes & Fees (12%)</span>
          <span>₹{(amount - (amount / 1.12)).toFixed(2)}</span>
        </div>
        {gstDetails.gstNumber && (
          <div className="flex justify-between text-sm mb-1 text-blue-600">
            <span>GST Details Included</span>
            <span>✓</span>
          </div>
        )}
        <div className="flex justify-between font-bold mt-2 pt-2 border-t">
          <span>Total</span>
          <span>₹{amount.toFixed(2)}</span>
        </div>
      </div>
      
      <div className="space-y-3">
        {/* Debug Info */}
        <div className="text-xs text-gray-500 p-2 bg-gray-100 rounded">
          <div>Env Key: {process.env.REACT_APP_RAZORPAY_KEY_ID ? 'Available' : 'Missing'}</div>
          <div>Amount: ₹{amount}</div>
        </div>
        
        <div className="flex justify-end">
          <button 
            onClick={handlePayment}
            disabled={loading} 
            className="bg-[#652220] text-white py-2 px-6 rounded-full disabled:opacity-70 w-full md:w-auto hover:bg-[#4e2222] transition-colors"
          >
            {loading ? "Loading RazorPay..." : "Pay with RazorPay"}
          </button>
        </div>
      </div>
    </div>
  );
};

const BookingForm = ({ selectedDates, onFormSubmit }) => {
  const [form] = Form.useForm();
  const [numOfNights, setNumOfNights] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [showGstForm, setShowGstForm] = useState(false);
  const [gstDetails, setGstDetails] = useState({
    gstNumber: '',
    companyName: '',
    companyAddress: ''
  });

  const BASE_PRICE_PER_NIGHT = parseInt(process.env.REACT_APP_PRICE_PER_NIGHT) || 45000;
  const TAX_RATE = 0.18;

  useEffect(() => {
    if (selectedDates && selectedDates.length === 2) {
      const startDate = dayjs(selectedDates[0]);
      const endDate = dayjs(selectedDates[1]);
      const nights = endDate.diff(startDate, "day");
      setNumOfNights(nights);

      const basePrice = nights * BASE_PRICE_PER_NIGHT;
      const tax = basePrice * TAX_RATE;
      const finalPrice = basePrice + tax;

      setTotalPrice(finalPrice);
    }
    
    // Reset payment success state when dates change
    setPaymentSuccess(false);
  }, [selectedDates]);
  
  const handlePaymentSuccess = async (response) => {
    setPaymentSuccess(true);
    setPaymentModalVisible(false);
    message.success('Payment verified! Creating your booking...');

    // Get form data
    const formValues = form.getFieldsValue();
    const customerDetails = getCustomerDetails();

    // Prepare booking data for our backend
    const bookingData = {
      checkIn: selectedDates[0],
      checkOut: selectedDates[1],
      guests: formValues.guests,
      name: `${formValues.firstName} ${formValues.lastName || ''}`.trim(),
      phone: formValues.phone,
    };

    try {
      // Send booking data to our backend to create calendar event
      const bookingResponse = await fetch('http://localhost:5002/api/bookings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bookingData),
      });

      const result = await bookingResponse.json();

      if (bookingResponse.ok) {
        message.success('Booking confirmed and added to calendar!');
        // Optionally, you can navigate to a confirmation page here
      } else {
        // Handle cases where booking to calendar might fail
        message.warning(result.message || 'Booking saved, but failed to update calendar.');
      }
    } catch (error) {
      console.error('Error creating booking:', error);
      message.error('An error occurred while finalizing your booking. Please contact support.');
    }
  };

  const handlePaymentFailure = (response) => {
    // Handle payment failure
  };

  const getCustomerDetails = () => {
    const formValues = form.getFieldsValue();
    return {
      firstName: formValues.firstName || '',
      lastName: formValues.lastName || '',
      email: formValues.email || '',
      phone: formValues.phone || '',
      nights: numOfNights,
    };
  };

  const handleProceed = () => {
    form.validateFields()
      .then(() => {
        if (!selectedDates || selectedDates.length !== 2) {
          message.warning('Please select check-in and check-out dates first.');
          return;
        }
        if (onFormSubmit) {
          onFormSubmit();
        } else {
          setPaymentModalVisible(true);
        }
      })
      .catch(errorInfo => {
        message.warning('Please fill in all required customer details first.');
      });
  };

  return (
    <div className="md:my-20 my-6">
      <div
        className="flex flex-col lg:flex-row gap-6 max-md:p-4 "
        style={{ maxWidth: "1440px", margin: "0 auto" }}
      >
        {/* Guest Details Section */}
        <div className="flex-1 bg-white  rounded-[30px] shadow-[0px_4px_20px_0px_rgba(0,0,0,0.16)]  border border-[#FFFFFF]">
          <h2 className="text-xl text-[#0A0A0A] font-bold p-4  md:p-8">
            GUEST DETAILS
          </h2>
          <hr />
          <div className="md:p-8 p-4">
            <Form form={form} layout="vertical" className="space-y-2" name="guestDetailsForm">
              <div className="flex gap-2">
                <Form.Item
                  name="firstName"
                  label={
                    <span className="text-[15px] poppins-regular">
                      First Name <span className="text-red-500">*</span>
                    </span>
                  }
                  className="col-span-4"
                  rules={[{ required: true, message: 'Please enter first name' }]}
                >
                  <Input
                    placeholder="Enter First Name"
                    className="text-[15px] poppins-regular"
                  />
                </Form.Item>
                <Form.Item
                  name="lastName"
                  label={
                    <span className="text-[15px] poppins-regular">
                      Last Name
                    </span>
                  }
                  className="col-span-4"
                >
                  <Input
                    placeholder="Enter Last Name"
                    className="text-[15px] poppins-regular"
                  />
                </Form.Item>
              </div>

              {/* Number of Guests */}
              <div className="mt-4">
                <Form.Item
                  name="guests"
                  label={
                    <span className="text-[15px] poppins-regular">
                      Number of Guests <span className="text-red-500">*</span>
                    </span>
                  }
                  rules={[{ required: true, message: 'Please enter the number of guests' }]}
                >
                  <Input
                    type="number"
                    placeholder="e.g., 2"
                    className="text-[15px] poppins-regular"
                  />
                </Form.Item>
              </div>

              {/* Email Address */}
              <div className="mt-4">
                <Form.Item
                  name="email"
                  label={
                    <span className="text-[15px] poppins-regular">
                      Email Address <span className="text-red-500">*</span>
                    </span>
                  }
                  rules={[
                    { required: true, message: 'Please enter email address' },
                    { type: 'email', message: 'Please enter a valid email address' }
                  ]}
                >
                  <Input
                    placeholder="Yourname@gmail.com"
                    className="text-[15px] poppins-regular"
                  />
                </Form.Item>
              </div>
              <div className="mt-4">
                <Form.Item
                  label={
                    <span className="text-[15px] poppins-regular">
                      Mobile Number <span className="text-red-500">*</span>
                    </span>
                  }
                  name="phone"
                  rules={[
                    { required: true, message: 'Please enter mobile number' },
                    { pattern: /^[0-9]{10}$/, message: 'Please enter a valid 10-digit mobile number' }
                  ]}
                >
                  <Input
                    className="text-[15px] poppins-regular"
                    addonBefore={
                      <Select defaultValue="91+" style={{ width: 80 }}>
                        <Select.Option value="91+">+91</Select.Option>
                        <Select.Option value="1+">+1</Select.Option>
                      </Select>
                    }
                    placeholder="Enter Your Number"
                  />
                </Form.Item>
              </div>

              {/* Message */}
            </Form>
          </div>
          <hr />
          <div className="p-4 md:p-8">
            <div className="flex items-center gap-2 mb-4">
              <input 
                type="checkbox" 
                className="w-5 h-5" 
                checked={showGstForm}
                onChange={(e) => setShowGstForm(e.target.checked)}
              />
              <label className="text-[#212121] poppins-regular">
                Enter GST Details{" "}
                <span className="text-[#212121] poppins-regular">(optional)</span>
              </label>
            </div>
            
            {showGstForm && (
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg border">
                <div className="space-y-4">
                  <div>
                    <label className="block text-[15px] poppins-regular mb-2">GST Number</label>
                    <Input
                      placeholder="Enter GST Number (e.g., 22AAAAA0000A1Z5)"
                      className="text-[15px] poppins-regular"
                      value={gstDetails.gstNumber}
                      onChange={(e) => setGstDetails({...gstDetails, gstNumber: e.target.value})}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-[15px] poppins-regular mb-2">Company/Business Name</label>
                    <Input
                      placeholder="Enter Company Name"
                      className="text-[15px] poppins-regular"
                      value={gstDetails.companyName}
                      onChange={(e) => setGstDetails({...gstDetails, companyName: e.target.value})}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-[15px] poppins-regular mb-2">Company Address</label>
                    <Input.TextArea
                      placeholder="Enter Company Address"
                      className="text-[15px] poppins-regular"
                      rows={3}
                      value={gstDetails.companyAddress}
                      onChange={(e) => setGstDetails({...gstDetails, companyAddress: e.target.value})}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Price Summary Section */}
        <div className="flex-1 bg-white  rounded-[30px] shadow-[0px_4px_20px_0px_rgba(0,0,0,0.16)]  border border-[#FFFFFF]">
          <h2 className="text-xl text-[#0A0A0A] font-bold p-4  md:p-8">
            Price Summary
          </h2>
          <hr />
          <div className="space-y-4 text-[#585858] text-lg  p-4 md:p-8">
            <div className="flex justify-between">
              <span className="text-base poppins-medium">Nights</span>
              <span className="text-base poppins-medium">{numOfNights}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#585858] text-base  md:text-lg poppins-medium">
                Base Price
              </span>
              <span className="text-base poppins-medium">
                ₹{(numOfNights * BASE_PRICE_PER_NIGHT).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#585858] text-base  md:text-lg poppins-medium">
                Taxes & Service Fees (18%)
              </span>
              <span className="text-base poppins-medium">
                ₹{(numOfNights * BASE_PRICE_PER_NIGHT * TAX_RATE).toFixed(2)}
              </span>
            </div>
            <hr className="my-4" />
            <div className="flex justify-between font-bold text-lg">
              <span className="text-[#0A0A0A] text-base  md:text-lg poppins-bold">
                Total Amount To Be Paid
              </span>
              <span className="text-base poppins-bold">
                ₹{totalPrice.toFixed(2)}
              </span>
            </div>
          </div>
          <div className="md:px-8 px-4">
            {/* Proceed Button */}
            <button 
              onClick={handleProceed}
              className={`w-full md:text-xl text-base poppins-medium py-3 rounded-full mt-4 shadow-md transition ${
                paymentSuccess 
                  ? "bg-green-600 hover:bg-green-700 text-white" 
                  : "bg-[#652220] hover:bg-[#4e2222] text-white"
              }`}
              disabled={paymentSuccess}
            >
              {paymentSuccess ? "✓ Payment Completed" : "Proceed to Payment Options"}
            </button>
            {paymentSuccess && (
              <p className="text-green-600 text-center mt-2 text-sm">
                Your booking has been confirmed. Thank you!
              </p>
            )}
          </div>
          
          {/* RazorPay Payment Modal */}
          <Modal
            title={
              <div className="flex items-center">
                <span className="text-xl font-bold text-[#652220]">Secure Checkout</span>
                <div className="ml-auto flex items-center space-x-2">
                  <img src="https://razorpay.com/assets/logos/rzp-logo-blue.svg" alt="RazorPay" className="h-6" />
                </div>
              </div>
            }
            open={paymentModalVisible}
            onCancel={() => setPaymentModalVisible(false)}
            footer={null}
            width={550}
            centered
            className="payment-modal"
          >
            <RazorPayCheckout 
              amount={totalPrice} 
              customerDetails={getCustomerDetails()}
              gstDetails={gstDetails}
              onSuccess={handlePaymentSuccess}
              onFailure={handlePaymentFailure}
            />
          </Modal>
          {/* Terms and Privacy Policy */}
          <div className="flex items-start gap-2 md:px-8 px-4 text-[#212121] font-normal text-[14px] md:text-sm mt-6 mb-6">
            <input type="checkbox" className="mt-1 w-5 h-5" />
            <p className="mt-1 poppins-regular">
              By proceeding, I agree to GirGlades{" "}
              <NavLink to="#" className="text-[#166bd3]  poppins-regular">
                Privacy Policy &{" "}
              </NavLink>{" "}
              <NavLink to="#" className=" text-[#166bd3]  poppins-regular">
                Terms of Service
              </NavLink>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingForm;
